import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Card, CardContent, Stack, Typography, Button, TextField, MenuItem,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer, Paper,
  Chip, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
  CircularProgress, Alert, Divider, Avatar, Grid, InputAdornment,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Calculate as ComputeIcon, Add as AddIcon, CheckCircle as ApproveIcon,
  Paid as PaidIcon, DeleteOutline as DeleteIcon, Print as PrintIcon,
  Receipt as PayslipIcon, ArrowBack as BackIcon, Close as CloseIcon,
  EditCalendar as PeriodIcon, Groups as HeadcountIcon,
  TrendingDown as DedIcon, AccountBalanceWallet as NetIcon,
  Refresh as RefreshIcon, HourglassEmpty as DraftIcon,
  CheckCircleOutline as ApprovedStateIcon, LocalAtm as PaidStateIcon,
} from '@mui/icons-material';
import API from '../../BaseURL';
import ModuleHeader from '../../components/ModuleHeader';
import swal from '../../utils/swal';

// ──────────────────────────────────────────────────────────────────
// Tiny helpers
// ──────────────────────────────────────────────────────────────────
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const MONTHS_LONG = ['January','February','March','April','May','June',
                     'July','August','September','October','November','December'];
const monthLabel = (y, m) => `${MONTHS[(m || 1) - 1]} ${y}`;
const fmt = (n) => Number(n || 0).toLocaleString('en-GH',
  { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtGhs = (n) => `GHS ${fmt(n)}`;

const STATUS_TONE = { draft: 'default', approved: 'info', paid: 'success' };
const STATUS_LABEL = { draft: 'Draft', approved: 'Approved', paid: 'Paid' };
const StatusChip = ({ status }) => (
  <Chip size="small" label={STATUS_LABEL[status] || status}
    color={STATUS_TONE[status] || 'default'}
    sx={{ fontWeight: 800, fontSize: 10.5, height: 20 }} />
);

// Shared PRO_TABLE styling.
const PRO_TABLE = {
  '& .MuiTableHead-root .MuiTableCell-root': {
    fontWeight: 800, fontSize: 10.5, letterSpacing: 0.6,
    textTransform: 'uppercase', color: 'text.secondary',
    bgcolor: '#f7f9fc', py: 1, borderBottom: '1px solid #d9dee6',
  },
  '& .MuiTableBody-root .MuiTableRow-root:nth-of-type(even)':
    { backgroundColor: '#fafbfc' },
  '& .MuiTableBody-root .MuiTableRow-root:hover':
    { backgroundColor: 'rgba(25,118,210,0.06) !important' },
  '& .MuiTableBody-root .MuiTableCell-root': {
    fontSize: 12.5, py: 1.05, borderBottom: '1px solid #eef0f4',
    fontVariantNumeric: 'tabular-nums',
  },
};

// ──────────────────────────────────────────────────────────────────
// Top-level page
// ──────────────────────────────────────────────────────────────────
export default function HRPayrollPage() {
  const [runs, setRuns]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [selected, setSel]    = useState(null);

  const [openNew, setOpenNew] = useState(false);
  const [slipOpen, setSlipOpen] = useState(false);
  const [slipData, setSlipData] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await API.get('/hr/payroll/runs');
      setRuns(r.data || []);
    } catch (e) {
      setError(e.response?.data?.message || 'Could not load payroll runs');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const loadDetail = async (id) => {
    setSel({ loading: true });
    try {
      const r = await API.get(`/hr/payroll/runs/${id}`);
      setSel(r.data);
    } catch (e) {
      setSel({ error: e.response?.data?.message || 'Could not load run' });
    }
  };

  const approve = async (run) => {
    const ok = await swal.confirm({
      title: 'Approve run',
      text: `Approve ${monthLabel(run.period_year, run.period_month)} payroll?`,
      tone: 'info',
    });
    if (!ok) return;
    try { await API.post(`/hr/payroll/runs/${run.id}/approve`); await load(); if (selected?.run?.id === run.id) loadDetail(run.id); }
    catch (e) { swal.error(e.response?.data?.message || 'Failed'); }
  };
  const markPaid = async (run) => {
    const ok = await swal.confirm({
      title: 'Mark paid',
      text: `Lock ${monthLabel(run.period_year, run.period_month)} as paid?`,
      tone: 'success',
    });
    if (!ok) return;
    try { await API.post(`/hr/payroll/runs/${run.id}/mark-paid`); await load(); if (selected?.run?.id === run.id) loadDetail(run.id); }
    catch (e) { swal.error(e.response?.data?.message || 'Failed'); }
  };
  const deleteRun = async (run) => {
    const ok = await swal.confirm({
      title: 'Delete draft',
      text: `Delete the draft run for ${monthLabel(run.period_year, run.period_month)}? Payslips will also be removed.`,
      tone: 'danger',
    });
    if (!ok) return;
    try { await API.delete(`/hr/payroll/runs/${run.id}`); await load(); setSel(null); }
    catch (e) { swal.error(e.response?.data?.message || 'Failed'); }
  };

  const openPayslip = async (id) => {
    setSlipOpen(true);
    setSlipData({ loading: true });
    try {
      const r = await API.get(`/hr/payslips/${id}`);
      setSlipData(r.data);
    } catch (e) { setSlipData({ error: e.response?.data?.message || 'Could not load' }); }
  };

  if (selected) {
    return (
      <>
        <RunDetail
          selected={selected}
          onBack={() => setSel(null)}
          onApprove={approve}
          onMarkPaid={markPaid}
          onDeleteDraft={deleteRun}
          onOpenPayslip={openPayslip} />
        <PayslipDialog open={slipOpen} data={slipData} onClose={() => setSlipOpen(false)} />
      </>
    );
  }

  return (
    <Box sx={{ px: { xs: 1, md: 1.5 }, py: 0.5 }}>
      <ModuleHeader
        title="Payroll"
        subtitle="Compute monthly runs, approve, mark paid, and view payslips"
        icon={<PayslipIcon />}
      />

      <RunsHero runs={runs} />

      <Card sx={{ borderRadius: 2, mt: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
        {/* Toolbar */}
        <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1.5,
          borderBottom: 1, borderColor: 'divider', bgcolor: '#fafbfc' }}>
          <PayslipIcon sx={{ fontSize: 20, color: 'primary.main' }} />
          <Typography fontWeight={800}>Monthly runs</Typography>
          <Chip size="small" variant="outlined" label={`${runs.length} run${runs.length === 1 ? '' : 's'}`} />
          <Box sx={{ flex: 1 }} />
          <Tooltip title="Reload">
            <span>
              <IconButton onClick={load} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpenNew(true)}
            sx={{ textTransform: 'none', fontWeight: 800, px: 2 }}>
            New payroll run
          </Button>
        </Box>

        {error && <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>}

        <TableContainer>
          <Table size="small" sx={PRO_TABLE}>
            <TableHead>
              <TableRow>
                <TableCell>Period</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="right">Headcount</TableCell>
                <TableCell align="right">Gross</TableCell>
                <TableCell align="right">Deductions</TableCell>
                <TableCell align="right">Net</TableCell>
                <TableCell>Created by</TableCell>
                <TableCell align="right" sx={{ width: 50 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                  <CircularProgress size={22} />
                </TableCell></TableRow>
              )}
              {!loading && runs.length === 0 && (
                <TableRow><TableCell colSpan={8} align="center"
                  sx={{ color: 'text.secondary', fontStyle: 'italic', py: 5 }}>
                  <PayslipIcon sx={{ fontSize: 36, color: 'text.disabled', mb: 1 }} />
                  <Typography>No payroll runs yet — click <b>New payroll run</b> to compute the first one.</Typography>
                </TableCell></TableRow>
              )}
              {!loading && runs.map(r => (
                <TableRow key={r.id} hover sx={{ cursor: 'pointer' }} onClick={() => loadDetail(r.id)}>
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={1.2}>
                      <Avatar sx={{
                        width: 32, height: 32, fontSize: 11.5, fontWeight: 800,
                        bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
                        color: 'primary.main',
                      }}>
                        {MONTHS[(r.period_month||1)-1]}
                      </Avatar>
                      <Box>
                        <Typography sx={{ fontWeight: 800, fontSize: 13.5, lineHeight: 1.2 }}>
                          {MONTHS_LONG[(r.period_month||1)-1]} {r.period_year}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Run #{r.id} · {r.created_at ? String(r.created_at).slice(0, 10) : '—'}
                        </Typography>
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell align="center"><StatusChip status={r.status} /></TableCell>
                  <TableCell align="right">{r.headcount}</TableCell>
                  <TableCell align="right">{fmt(r.gross_total)}</TableCell>
                  <TableCell align="right" sx={{ color: 'error.main' }}>{fmt(r.deduction_total)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800, color: 'success.main' }}>{fmt(r.net_total)}</TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>{r.created_by || '—'}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Open run"><IconButton size="small"><PayslipIcon fontSize="small" /></IconButton></Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      <NewRunDialog open={openNew} onClose={() => setOpenNew(false)}
        onCreated={(id) => { setOpenNew(false); load(); if (id) loadDetail(id); }} />
      <PayslipDialog open={slipOpen} data={slipData} onClose={() => setSlipOpen(false)} />
    </Box>
  );
}

// ──────────────────────────────────────────────────────────────────
// Runs hero — 4 stat tiles
// ──────────────────────────────────────────────────────────────────
function RunsHero({ runs }) {
  const stats = useMemo(() => {
    const by = (s) => runs.filter(r => r.status === s);
    const totalNet = runs.reduce((a, r) => a + Number(r.net_total || 0), 0);
    return {
      total:    runs.length,
      draft:    by('draft').length,
      approved: by('approved').length,
      paid:     by('paid').length,
      totalNet,
    };
  }, [runs]);

  return (
    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.2}>
      <HeroTile icon={<PayslipIcon />}        label="Total runs"     value={stats.total}                tone="primary" />
      <HeroTile icon={<DraftIcon />}          label="Draft"          value={stats.draft}                tone="warning"
        sub="Awaiting approval" />
      <HeroTile icon={<ApprovedStateIcon />}  label="Approved"       value={stats.approved}             tone="info"
        sub="Locked, not yet paid" />
      <HeroTile icon={<PaidStateIcon />}      label="Paid"           value={stats.paid}                 tone="success" />
      <HeroTile icon={<NetIcon />}            label="Lifetime net"   value={fmtGhs(stats.totalNet)}     tone="secondary" wide />
    </Stack>
  );
}

function HeroTile({ icon, label, value, tone = 'primary', sub, wide }) {
  return (
    <Card sx={{ flex: wide ? 1.5 : 1, minWidth: 0, borderRadius: 2,
      borderLeft: 4, borderColor: `${tone}.main`,
      boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
      <CardContent sx={{ display: 'flex', gap: 1.5, alignItems: 'center', py: 1.6, '&:last-child': { pb: 1.6 } }}>
        <Box sx={{
          width: 42, height: 42, borderRadius: 1.5, flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: (t) => alpha(t.palette[tone].main, 0.12),
          color: `${tone}.main`,
        }}>{icon}</Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="caption" color="text.secondary"
            sx={{ display: 'block', fontWeight: 700, letterSpacing: 0.5,
                  textTransform: 'uppercase', fontSize: 10, lineHeight: 1.2 }}>
            {label}
          </Typography>
          <Typography sx={{ fontWeight: 800, fontSize: 18, lineHeight: 1.25 }} noWrap>
            {value}
          </Typography>
          {sub && (
            <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: 10.5 }}>
              {sub}
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}

// ──────────────────────────────────────────────────────────────────
// Run detail view
// ──────────────────────────────────────────────────────────────────
function RunDetail({ selected, onBack, onApprove, onMarkPaid, onDeleteDraft, onOpenPayslip }) {
  const run = selected.run;
  const slips = selected.payslips || [];

  // Fetch school info once for the bulk-print template.
  const [school, setSchool] = useState({});
  useEffect(() => {
    API.get('/setup/info').then(r => setSchool(r.data?.info || {})).catch(() => {});
  }, []);
  const printAll = () => {
    if (!slips.length) return;
    openPayslipPrint(slips, run, school);
  };

  return (
    <Box sx={{ px: { xs: 1, md: 1.5 }, py: 0.5 }}>
      <ModuleHeader
        title="Payroll run"
        subtitle={run ? `${MONTHS_LONG[(run.period_month||1)-1]} ${run.period_year}` : ''}
        icon={<PayslipIcon />}
      />

      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <Button startIcon={<BackIcon />} onClick={onBack}
          sx={{ textTransform: 'none', fontWeight: 700 }}>
          Back to runs
        </Button>
        <Box sx={{ flex: 1 }} />
        {run?.status === 'draft' && (
          <>
            <Button color="error" variant="outlined" startIcon={<DeleteIcon />}
              onClick={() => onDeleteDraft(run)} sx={{ textTransform: 'none', fontWeight: 700 }}>
              Delete draft
            </Button>
            <Button color="info" variant="contained" startIcon={<ApproveIcon />}
              onClick={() => onApprove(run)} sx={{ textTransform: 'none', fontWeight: 800, px: 2.5 }}>
              Approve
            </Button>
          </>
        )}
        {run?.status === 'approved' && (
          <Button color="success" variant="contained" startIcon={<PaidIcon />}
            onClick={() => onMarkPaid(run)} sx={{ textTransform: 'none', fontWeight: 800, px: 2.5 }}>
            Mark paid
          </Button>
        )}
      </Stack>

      {selected.loading && <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress /></Box>}
      {selected.error && <Alert severity="error">{selected.error}</Alert>}

      {run && (
        <>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.2} sx={{ mb: 2 }}>
            <HeroTile icon={<HeadcountIcon />} label="Headcount"  value={run.headcount} tone="primary" />
            <HeroTile icon={<NetIcon />}       label="Gross"      value={fmtGhs(run.gross_total)} tone="info" />
            <HeroTile icon={<DedIcon />}       label="Deductions" value={fmtGhs(run.deduction_total)} tone="error"
              sub="SSNIT + PAYE + Other" />
            <HeroTile icon={<PaidIcon />}      label="Net payout" value={fmtGhs(run.net_total)} tone="success" wide />
          </Stack>

          <Card sx={{ borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
            <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider', bgcolor: '#fafbfc',
              display: 'flex', alignItems: 'center', gap: 1 }}>
              <PayslipIcon sx={{ fontSize: 20, color: 'primary.main' }} />
              <Typography fontWeight={800}>Payslips ({slips.length})</Typography>
              <Box sx={{ flex: 1 }} />
              <Tooltip title="Print every payslip on this run in one document">
                <span>
                  <Button startIcon={<PrintIcon />} variant="outlined" size="small"
                    onClick={printAll} disabled={slips.length === 0}
                    sx={{ textTransform: 'none', fontWeight: 700, mr: 1 }}>
                    Print all payslips
                  </Button>
                </span>
              </Tooltip>
              <StatusChip status={run.status} />
            </Box>
            <TableContainer>
              <Table size="small" sx={PRO_TABLE}>
                <TableHead>
                  <TableRow>
                    <TableCell>Employee</TableCell>
                    <TableCell>Department · Type</TableCell>
                    <TableCell align="right">Basic</TableCell>
                    <TableCell align="right">Allowances</TableCell>
                    <TableCell align="right">Gross</TableCell>
                    <TableCell align="right">SSNIT</TableCell>
                    <TableCell align="right">PAYE</TableCell>
                    <TableCell align="right">Net</TableCell>
                    <TableCell align="right" sx={{ width: 50 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {slips.length === 0 && (
                    <TableRow><TableCell colSpan={9} align="center"
                      sx={{ color: 'text.disabled', fontStyle: 'italic', py: 4 }}>
                      No payslips on this run.
                    </TableCell></TableRow>
                  )}
                  {slips.map(p => (
                    <TableRow key={p.id} hover>
                      <TableCell sx={{ fontWeight: 700 }}>
                        {p.employee_name}
                        {p.notes && (
                          <Typography variant="caption" sx={{ display: 'block',
                            color: 'text.disabled', fontStyle: 'italic', fontSize: 10.5 }}>
                            {p.notes}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary' }}>
                        {p.department || '—'}
                        {p.employment_type && (
                          <Chip size="small" label={p.employment_type}
                            variant="outlined" sx={{ ml: 0.6, height: 18, fontSize: 10 }} />
                        )}
                      </TableCell>
                      <TableCell align="right">{fmt(p.basic)}</TableCell>
                      <TableCell align="right">{fmt(p.allowances)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{fmt(p.gross)}</TableCell>
                      <TableCell align="right" sx={{ color: Number(p.ssnit_employee) > 0 ? 'error.main' : 'text.disabled' }}>
                        {Number(p.ssnit_employee) > 0 ? fmt(p.ssnit_employee) : 'Exempt'}
                      </TableCell>
                      <TableCell align="right" sx={{ color: Number(p.paye) > 0 ? 'error.main' : 'text.disabled' }}>
                        {Number(p.paye) > 0 ? fmt(p.paye) : '—'}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 800, color: 'success.main' }}>{fmt(p.net)}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Open payslip">
                          <IconButton size="small" onClick={() => onOpenPayslip(p.id)}>
                            <PayslipIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </>
      )}
    </Box>
  );
}

// ──────────────────────────────────────────────────────────────────
// New-run dialog — gradient header, year/month picker, live preview
// ──────────────────────────────────────────────────────────────────
function NewRunDialog({ open, onClose, onCreated }) {
  const today = new Date();
  const [year, setYear]   = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [preview, setPreview]     = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [creating, setCreating]   = useState(false);
  const [err, setErr]             = useState('');

  // Reset state when dialog opens.
  useEffect(() => {
    if (open) {
      setPreview(null); setErr('');
      setYear(today.getFullYear());
      setMonth(today.getMonth() + 1);
    }
    // eslint-disable-next-line
  }, [open]);

  const runPreview = async () => {
    setPreviewing(true); setErr(''); setPreview(null);
    try {
      const r = await API.post('/hr/payroll/preview');
      setPreview(r.data);
    } catch (e) {
      setErr(e.response?.data?.message || 'Preview failed');
    } finally { setPreviewing(false); }
  };

  const create = async () => {
    setCreating(true); setErr('');
    try {
      const r = await API.post('/hr/payroll/runs',
        { period_year: Number(year), period_month: Number(month) });
      onCreated?.(r.data?.id);
    } catch (e) {
      setErr(e.response?.data?.message || 'Create failed');
    } finally { setCreating(false); }
  };

  const totals = preview?.totals;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}>
      {/* Gradient header */}
      <Box sx={{
        px: 3, py: 2,
        background: (t) => `linear-gradient(135deg, ${t.palette.primary.main} 0%, ${t.palette.secondary.main} 100%)`,
        color: '#fff', display: 'flex', alignItems: 'center', gap: 1.5,
      }}>
        <Avatar sx={{
          width: 44, height: 44, bgcolor: 'rgba(255,255,255,0.22)',
          border: '1.5px solid rgba(255,255,255,0.3)',
        }}>
          <ComputeIcon />
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 800, fontSize: 16, lineHeight: 1.2 }}>
            New payroll run
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.9 }}>
            Pick the period, preview the numbers, then commit the draft for approval
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: '#fff' }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent dividers sx={{ bgcolor: '#fafbfc', p: 2.5 }}>
        {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}

        {/* ── Period card ────────────────────────────────────── */}
        <Card variant="outlined" sx={{ borderRadius: 2, mb: 2 }}>
          <Box sx={{ px: 2, py: 1.2, borderBottom: 1, borderColor: 'divider',
            display: 'flex', alignItems: 'center', gap: 1.2, bgcolor: '#fff' }}>
            <Box sx={{
              width: 34, height: 34, borderRadius: 1,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
              color: 'primary.main',
            }}><PeriodIcon /></Box>
            <Box>
              <Typography fontWeight={800} fontSize={13.5}>Pay period</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10.5 }}>
                Currently computing for <b>{MONTHS_LONG[(month||1)-1]} {year}</b>
              </Typography>
            </Box>
          </Box>
          <Box sx={{ p: 2 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
              <TextField select size="small" label="Year" value={year}
                onChange={(e) => setYear(e.target.value)} sx={{ minWidth: 140 }}>
                {[1,0,-1,-2].map(d => {
                  const y = today.getFullYear() + d;
                  return <MenuItem key={y} value={y}>{y}</MenuItem>;
                })}
              </TextField>
              <TextField select size="small" label="Month" value={month}
                onChange={(e) => setMonth(e.target.value)} sx={{ minWidth: 180 }}>
                {MONTHS_LONG.map((m, i) => <MenuItem key={i+1} value={i+1}>{m}</MenuItem>)}
              </TextField>
              <Box sx={{ flex: 1 }} />
              <Button variant="outlined" startIcon={<ComputeIcon />}
                onClick={runPreview} disabled={previewing}
                sx={{ textTransform: 'none', fontWeight: 800, px: 2 }}>
                {previewing ? 'Computing…' : (preview ? 'Re-preview' : 'Preview')}
              </Button>
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Preview reads every active employee, applies the current SSNIT and PAYE
              settings, and shows totals + per-employee rows below. Nothing is saved until
              you click <b>Create draft run</b>.
            </Typography>
          </Box>
        </Card>

        {/* ── Preview tiles + table ──────────────────────────── */}
        {!preview && !previewing && (
          <Box sx={{ p: 5, textAlign: 'center', border: '1px dashed', borderColor: 'divider',
            borderRadius: 2, color: 'text.disabled' }}>
            <ComputeIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
            <Typography>Click <b>Preview</b> to compute what this period would look like.</Typography>
          </Box>
        )}

        {previewing && (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <CircularProgress />
            <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
              Computing payslips…
            </Typography>
          </Box>
        )}

        {preview && totals && (
          <>
            <Grid container spacing={1.2} sx={{ mb: 2 }}>
              <Grid item xs={6} sm={3}>
                <TotalsTile icon={<HeadcountIcon />} label="Headcount" value={totals.headcount}    tone="primary" />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TotalsTile icon={<NetIcon />}       label="Gross"     value={fmtGhs(totals.gross)} tone="info" />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TotalsTile icon={<DedIcon />}       label="Deductions" value={fmtGhs(totals.deduction_total)} tone="error" />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TotalsTile icon={<PaidIcon />}      label="Net"       value={fmtGhs(totals.net)}   tone="success" />
              </Grid>
            </Grid>

            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider',
                display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#fafbfc' }}>
                <Typography fontWeight={800} fontSize={13}>Per-employee preview</Typography>
                <Box sx={{ flex: 1 }} />
                <Typography variant="caption" color="text.secondary">
                  {preview.payslips.length} payslip(s)
                </Typography>
              </Box>
              <TableContainer sx={{ maxHeight: 360 }}>
                <Table size="small" stickyHeader sx={PRO_TABLE}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Employee</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell align="right">Gross</TableCell>
                      <TableCell align="right">SSNIT</TableCell>
                      <TableCell align="right">PAYE</TableCell>
                      <TableCell align="right">Net</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {preview.payslips.length === 0 && (
                      <TableRow><TableCell colSpan={6} align="center"
                        sx={{ color: 'text.disabled', fontStyle: 'italic', py: 4 }}>
                        No active employees found. Add some under <b>Employees</b> first.
                      </TableCell></TableRow>
                    )}
                    {preview.payslips.map(p => (
                      <TableRow key={p.employee_id} hover>
                        <TableCell sx={{ fontWeight: 700 }}>
                          {p.employee_name}
                          {p.notes && (
                            <Typography variant="caption" sx={{ display: 'block',
                              color: 'text.disabled', fontStyle: 'italic', fontSize: 10.5 }}>
                              {p.notes}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {p.employment_type
                            ? <Chip size="small" label={p.employment_type} variant="outlined"
                                sx={{ height: 18, fontSize: 10 }} />
                            : <span style={{ color: '#bdbdbd' }}>—</span>}
                        </TableCell>
                        <TableCell align="right">{fmt(p.gross)}</TableCell>
                        <TableCell align="right" sx={{ color: Number(p.ssnit_employee) > 0 ? 'error.main' : 'text.disabled' }}>
                          {Number(p.ssnit_employee) > 0 ? fmt(p.ssnit_employee) : 'Exempt'}
                        </TableCell>
                        <TableCell align="right" sx={{ color: Number(p.paye) > 0 ? 'error.main' : 'text.disabled' }}>
                          {Number(p.paye) > 0 ? fmt(p.paye) : '—'}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 800, color: 'success.main' }}>{fmt(p.net)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.8, bgcolor: 'background.paper',
        borderTop: 1, borderColor: 'divider' }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', fontWeight: 600 }}>
          Cancel
        </Button>
        <Box sx={{ flex: 1 }} />
        {!preview && (
          <Typography variant="caption" color="text.secondary" sx={{ mr: 2 }}>
            Preview required before creating the run
          </Typography>
        )}
        <Button variant="contained" onClick={create} disabled={creating || !preview}
          size="large" startIcon={<AddIcon />}
          sx={{ textTransform: 'none', fontWeight: 800, px: 3 }}>
          {creating ? 'Creating…' : 'Create draft run'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function TotalsTile({ icon, label, value, tone }) {
  return (
    <Box sx={{ p: 1.2, borderRadius: 1.5,
      bgcolor: (t) => alpha(t.palette[tone].main, 0.08),
      border: '1px solid', borderColor: (t) => alpha(t.palette[tone].main, 0.25),
      display: 'flex', gap: 1.2, alignItems: 'center',
    }}>
      <Box sx={{
        width: 32, height: 32, borderRadius: 1, flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        bgcolor: `${tone}.main`, color: '#fff',
      }}>{icon}</Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase',
          letterSpacing: 0.5, color: 'text.secondary', fontSize: 10, lineHeight: 1.2 }}>
          {label}
        </Typography>
        <Typography sx={{ fontWeight: 800, fontSize: 14.5, color: `${tone}.dark`, lineHeight: 1.2 }} noWrap>
          {value}
        </Typography>
      </Box>
    </Box>
  );
}

// ──────────────────────────────────────────────────────────────────
// Payslip viewer dialog — printable. Kept compact, same logic as before.
// ──────────────────────────────────────────────────────────────────
function PayslipDialog({ open, data, onClose }) {
  const [school, setSchool] = useState({});
  useEffect(() => {
    if (open) API.get('/setup/info').then(r => setSchool(r.data?.info || {})).catch(() => {});
  }, [open]);

  const print = () => {
    if (!data?.payslip) return;
    openPayslipPrint([data.payslip], data.run, school);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}>
      <Box sx={{
        px: 3, py: 2,
        background: (t) => `linear-gradient(135deg, ${t.palette.success.main} 0%, ${t.palette.success.dark} 100%)`,
        color: '#fff', display: 'flex', alignItems: 'center', gap: 1.5,
      }}>
        <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.22)', width: 40, height: 40 }}>
          <PayslipIcon />
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 800, fontSize: 16, lineHeight: 1.2 }} noWrap>
            Payslip
          </Typography>
          {data?.payslip && (
            <Typography variant="caption" sx={{ opacity: 0.9 }} noWrap>
              {data.payslip.employee_name} · {monthLabel(data.run?.period_year, data.run?.period_month)}
            </Typography>
          )}
        </Box>
        <IconButton onClick={onClose} sx={{ color: '#fff' }}>
          <CloseIcon />
        </IconButton>
      </Box>
      <DialogContent dividers sx={{ bgcolor: '#fafbfc', p: 2.5 }}>
        {data?.loading && <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress /></Box>}
        {data?.error   && <Alert severity="error">{data.error}</Alert>}
        {data?.payslip && (
          <Box>
            <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#fff', mb: 2,
              border: '1px solid', borderColor: 'divider' }}>
              <Typography fontWeight={800} fontSize={16}>{data.payslip.employee_name}</Typography>
              <Typography variant="caption" color="text.secondary">
                {data.payslip.department || '—'} · {data.payslip.position || '—'}
              </Typography>
            </Box>
            <Grid container spacing={1.2}>
              <Grid item xs={6}>
                <Box sx={{ p: 1.4, borderRadius: 1.5, bgcolor: '#fff', border: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="caption" color="text.secondary"
                    sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 10 }}>
                    Earnings
                  </Typography>
                  <Row label="Basic"      value={fmt(data.payslip.basic)} />
                  <Row label="Allowances" value={fmt(data.payslip.allowances)} />
                  <Divider sx={{ my: 0.5 }} />
                  <Row label="Gross"      value={fmt(data.payslip.gross)} bold />
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box sx={{ p: 1.4, borderRadius: 1.5, bgcolor: '#fff', border: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="caption" color="text.secondary"
                    sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 10 }}>
                    Deductions
                  </Typography>
                  <Row label="SSNIT (employee)" value={fmt(data.payslip.ssnit_employee)} negative />
                  <Row label="PAYE"             value={fmt(data.payslip.paye)} negative />
                  <Row label="Other"            value={fmt(data.payslip.other_deductions)} negative />
                  <Divider sx={{ my: 0.5 }} />
                  <Row label="Total"            value={fmt(data.payslip.deduction_total)} bold negative />
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ p: 2, borderRadius: 2,
                  bgcolor: (t) => alpha(t.palette.success.main, 0.10),
                  border: '2px solid', borderColor: 'success.main',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography sx={{ fontWeight: 900, fontSize: 14, color: 'success.dark' }}>
                    NET PAY
                  </Typography>
                  <Typography sx={{ fontWeight: 900, fontSize: 20, color: 'success.dark' }}>
                    {fmtGhs(data.payslip.net)}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.disabled">
                  Employer SSNIT (not deducted): {fmtGhs(data.payslip.ssnit_employer)}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', fontWeight: 600 }}>Close</Button>
        <Box sx={{ flex: 1 }} />
        <Button variant="contained" startIcon={<PrintIcon />} onClick={print}
          disabled={!data?.payslip}
          sx={{ textTransform: 'none', fontWeight: 800, px: 3 }}>
          Print
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function Row({ label, value, bold, negative }) {
  return (
    <Stack direction="row" justifyContent="space-between"
      sx={{ fontSize: 13, py: 0.4,
            fontWeight: bold ? 800 : 500,
            color: negative ? 'error.main' : 'text.primary' }}>
      <span>{label}</span><span>{value}</span>
    </Stack>
  );
}

// ──────────────────────────────────────────────────────────────────
// openPayslipPrint — shared printer used by single-payslip dialog +
// the bulk "Print all" button on the run detail. Renders a polished
// A4 template per payslip, mirroring the Ghana-government payslip
// layout: school header (logo + name + address), pay-period bar,
// employee block (with SSNIT/TIN/bank), itemised earnings list,
// statutory deductions broken out (SSNIT Tier 1, Tier 2, PAYE),
// PAYE bracket-by-bracket calculation table, employer contributions
// row, NET PAY box, signature lines, and a translucent watermark.
// ──────────────────────────────────────────────────────────────────
export function openPayslipPrint(payslips, run, school) {
  const safe = (v) => String(v ?? '').replace(/[&<>"']/g, (c) =>
    ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[c]));
  const fmtN = (n) => Number(n || 0).toLocaleString('en-GH',
    { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const periodLabel = run
    ? `${MONTHS_LONG[(run.period_month || 1) - 1]} ${run.period_year}`
    : '';
  const logo  = school?.logo || '';
  const name  = safe(school?.school_name || 'School Management System');
  const motto = safe(school?.motto || '');
  const addr  = safe(school?.address || '');
  const phone = safe(school?.phone || '');
  const email = safe(school?.email || '');

  const pageFor = (p) => {
    const lines = Array.isArray(p.allowance_lines) ? p.allowance_lines : [];
    const paye  = Array.isArray(p.paye_breakdown)  ? p.paye_breakdown  : [];
    const tier2Emp = Number(p.tier2_employee || 0);
    const tier2Er  = Number(p.tier2_employer || 0);
    const hasTier2 = tier2Emp > 0 || tier2Er > 0;

    // PAYE breakdown — bracket-by-bracket. Hidden when no PAYE.
    const payeRows = paye.length === 0
      ? `<tr><td colspan="4" class="muted">PAYE within tax-free band — no income tax due.</td></tr>`
      : paye.map(b => `
          <tr>
            <td>${safe(b.description || `Band ${b.order}`)}
              <span class="muted small">${fmtN(b.lower)} – ${b.upper == null ? '∞' : fmtN(b.upper)}</span>
            </td>
            <td class="amt">${fmtN(b.slice)}</td>
            <td class="amt">${Number(b.rate).toFixed(2)} %</td>
            <td class="amt">${fmtN(b.tax)}</td>
          </tr>`).join('');

    // Employer-side block: shows what the school contributes on top
    // (informational — not deducted from net).
    const employerTotal = Number(p.ssnit_employer || 0) + Number(p.tier2_employer || 0);

    // Earnings tabular rows for the Vertex42-style four-column table.
    // Base / Rate columns are used when the amount is percentage-based
    // (rate% × basic); otherwise Base + Rate stay em-dash. YTD mirrors
    // the current period until the system starts tracking cumulative
    // year totals — a blank YTD column looks worse than repeating the
    // current-period value.
    const basic = Number(p.basic || 0);
    const earningsBody = [
      `<tr>
         <td>Basic salary</td>
         <td class="amt">—</td>
         <td class="amt">—</td>
         <td class="amt">${fmtN(basic)}</td>
         <td class="amt ytd">${fmtN(basic)}</td>
       </tr>`,
      ...(lines.length > 0
        ? lines.map(l => {
            const isPct = String(l.mode || '').toLowerCase() === 'percentage';
            const rate  = isPct ? `${Number(l.percentage || 0).toFixed(2)} %` : '—';
            const base  = isPct ? fmtN(basic) : '—';
            const tags  = [l.taxable && 'Taxable', l.pensionable && 'Pensionable']
              .filter(Boolean).map(t => `<span class="tag ${t.toLowerCase()}">${t}</span>`).join('');
            return `<tr>
              <td>${safe(l.name)}${tags ? ' ' + tags : ''}</td>
              <td class="amt">${base}</td>
              <td class="amt">${rate}</td>
              <td class="amt">${fmtN(l.amount)}</td>
              <td class="amt ytd">${fmtN(l.amount)}</td>
            </tr>`;
          })
        : [`<tr>
             <td>Allowances</td>
             <td class="amt">—</td>
             <td class="amt">—</td>
             <td class="amt">${fmtN(p.allowances)}</td>
             <td class="amt ytd">${fmtN(p.allowances)}</td>
           </tr>`]),
      `<tr class="grand">
         <td colspan="3">GROSS PAY</td>
         <td class="amt">${fmtN(p.gross)}</td>
         <td class="amt ytd">${fmtN(p.gross)}</td>
       </tr>`,
    ].join('');

    // Deductions body — same two-column (Current + YTD) treatment as
    // the reference template.
    const dedBody = [
      `<tr>
         <td>SSNIT Tier 1 — employee (5.5%)</td>
         <td class="amt">${fmtN(p.ssnit_employee)}</td>
         <td class="amt ytd">${fmtN(p.ssnit_employee)}</td>
       </tr>`,
      hasTier2 ? `<tr>
         <td>SSNIT Tier 2 — employee</td>
         <td class="amt">${fmtN(tier2Emp)}</td>
         <td class="amt ytd">${fmtN(tier2Emp)}</td>
       </tr>` : '',
      `<tr>
         <td>PAYE income tax</td>
         <td class="amt">${fmtN(p.paye)}</td>
         <td class="amt ytd">${fmtN(p.paye)}</td>
       </tr>`,
      Number(p.other_deductions) > 0
        ? `<tr>
             <td>Other deductions</td>
             <td class="amt">${fmtN(p.other_deductions)}</td>
             <td class="amt ytd">${fmtN(p.other_deductions)}</td>
           </tr>` : '',
      `<tr class="grand">
         <td>TOTAL DEDUCTIONS</td>
         <td class="amt">${fmtN(p.deduction_total)}</td>
         <td class="amt ytd">${fmtN(p.deduction_total)}</td>
       </tr>`,
    ].filter(Boolean).join('');

    return `
    <section class="page">
      ${logo ? `<div class="watermark" style="background-image:url('${logo}')"></div>` : ''}

      <!-- Header — school on the left, big PAYSLIP wordmark on the right -->
      <header class="hdr">
        <div class="hdr-left">
          ${logo ? `<img class="logo" src="${logo}" alt="logo" />` : ''}
          <div class="hdr-text">
            <div class="school">${name}</div>
            ${motto ? `<div class="motto">${motto}</div>` : ''}
            ${addr  ? `<div class="meta">${addr}</div>` : ''}
            <div class="meta">${[phone && 'Phone: ' + phone, email && 'Email: ' + email].filter(Boolean).join(' · ')}</div>
          </div>
        </div>
        <div class="hdr-tag">PAYSLIP</div>
      </header>

      <!-- Employee info card + pay-period mini grid, laid out
           side-by-side like the Vertex42 template. -->
      <div class="top">
        <div class="emp-card">
          <div class="band">EMPLOYEE INFORMATION</div>
          <div class="emp-body">
            <div class="emp-name">${safe(p.employee_name)}</div>
            <div class="emp-line">${safe(p.department || '—')} · ${safe(p.position || '—')}</div>
            <div class="emp-line">${safe(p.employment_type || '—')}${p.hire_date ? ' · Hired ' + safe(String(p.hire_date).slice(0,10)) : ''}</div>
            ${p.employee_email ? `<div class="emp-line small">Email: ${safe(p.employee_email)}</div>` : ''}
            ${p.employee_phone ? `<div class="emp-line small">Phone: ${safe(p.employee_phone)}</div>` : ''}
          </div>
        </div>
        <div class="paygrid">
          <div class="paygrid-row band-row">
            <div class="cell">PAY PERIOD</div>
            <div class="cell">PAY TYPE</div>
            <div class="cell">PAYSLIP #</div>
          </div>
          <div class="paygrid-row">
            <div class="cell v">${safe(periodLabel)}</div>
            <div class="cell v">Monthly</div>
            <div class="cell v">${safe(p.id ?? '')}</div>
          </div>
          <div class="paygrid-row band-row">
            <div class="cell">STAFF ID</div>
            <div class="cell">SSNIT NO.</div>
            <div class="cell">TIN</div>
          </div>
          <div class="paygrid-row">
            <div class="cell v">${safe(p.employee_code || p.employee_id || '—')}</div>
            <div class="cell v">${safe(p.ssnit_number || '—')}</div>
            <div class="cell v">${safe(p.tin_number || '—')}</div>
          </div>
        </div>
      </div>

      <div class="pay-method">
        <span class="pm-lbl">Payment Method:</span>
        <span class="pm-v">${safe(p.payroll_mode || '—')}${p.bank_name ? ' · ' + safe(p.bank_name) : ''}${p.account_number ? ' · A/C ' + safe(p.account_number) : ''}</span>
      </div>

      <!-- EARNINGS — Vertex42 four-column layout: item, base, rate,
           current, ytd. Percentage allowances show basic × rate; fixed
           ones leave base/rate as em-dashes. -->
      <table class="ledger earn">
        <colgroup>
          <col style="width:44%" />
          <col style="width:14%" />
          <col style="width:10%" />
          <col style="width:16%" />
          <col style="width:16%" />
        </colgroup>
        <thead>
          <tr>
            <th>EARNINGS</th>
            <th class="amt">BASE</th>
            <th class="amt">RATE</th>
            <th class="amt">CURRENT</th>
            <th class="amt ytd">YTD</th>
          </tr>
        </thead>
        <tbody>${earningsBody}</tbody>
      </table>

      <!-- DEDUCTIONS — three-column (item, current, YTD) matching the
           reference. TOTAL DEDUCTIONS row uses the same grand-total
           treatment as GROSS PAY. -->
      <table class="ledger ded">
        <colgroup>
          <col style="width:68%" />
          <col style="width:16%" />
          <col style="width:16%" />
        </colgroup>
        <thead>
          <tr>
            <th>DEDUCTIONS</th>
            <th class="amt">CURRENT</th>
            <th class="amt ytd">YTD</th>
          </tr>
        </thead>
        <tbody>${dedBody}</tbody>
      </table>

      <!-- PAYE bracket walk — preserved from the previous template so
           the audit trail (which bracket contributed how much) is still
           visible on every printed slip. -->
      <table class="ledger paye">
        <thead><tr>
          <th>PAYE COMPUTATION (bracket walk)</th>
          <th class="amt">Slice in band</th>
          <th class="amt">Rate</th>
          <th class="amt">Tax</th>
        </tr></thead>
        <tbody>
          <tr class="meta-row"><td colspan="4">
            Taxable income base = Gross ${Number(p.allowances) ? `(GHS ${fmtN(p.gross)})` : ''}
            ${Number(p.ssnit_employee) > 0 ? `&nbsp;−&nbsp; SSNIT Tier 1 (GHS ${fmtN(p.ssnit_employee)})` : ''}
            ${hasTier2 ? `&nbsp;−&nbsp; Tier 2 (GHS ${fmtN(tier2Emp)})` : ''}
            &nbsp;=&nbsp; <b>GHS ${fmtN(p.paye_taxable_base)}</b>
          </td></tr>
          ${payeRows}
          <tr class="tot"><td colspan="3">TOTAL PAYE</td><td class="amt">${fmtN(p.paye)}</td></tr>
        </tbody>
      </table>

      <!-- NET PAY — spans full width with a right-aligned amount box,
           mirroring the reference template's final line. -->
      <table class="ledger net">
        <colgroup>
          <col style="width:68%" />
          <col style="width:16%" />
          <col style="width:16%" />
        </colgroup>
        <tbody>
          <tr>
            <td class="net-l">NET PAY</td>
            <td class="amt net-v">GHS ${fmtN(p.net)}</td>
            <td class="amt net-v ytd">GHS ${fmtN(p.net)}</td>
          </tr>
        </tbody>
      </table>

      <!-- EMPLOYER CONTRIBUTION — informational, not deducted from net. -->
      <table class="ledger employer">
        <colgroup>
          <col style="width:68%" />
          <col style="width:32%" />
        </colgroup>
        <thead>
          <tr>
            <th>EMPLOYER CONTRIBUTION (not deducted from net)</th>
            <th class="amt">AMOUNT</th>
          </tr>
        </thead>
        <tbody>
          <tr><td>SSNIT Tier 1 — employer (13%)</td><td class="amt">${fmtN(p.ssnit_employer)}</td></tr>
          ${hasTier2 ? `<tr><td>SSNIT Tier 2 — employer</td><td class="amt">${fmtN(tier2Er)}</td></tr>` : ''}
          <tr class="grand"><td>TOTAL EMPLOYER COST</td><td class="amt">${fmtN(Number(p.gross) + employerTotal)}</td></tr>
        </tbody>
      </table>

      ${p.notes ? `<div class="footnote"><i>${safe(p.notes)}</i></div>` : ''}

      <div class="sigs">
        <div class="sig"><div class="line"></div><div class="lbl">Employee signature</div></div>
        <div class="sig"><div class="line"></div><div class="lbl">HR / Bursar signature</div></div>
        <div class="sig"><div class="line"></div><div class="lbl">Date</div></div>
      </div>

      <div class="legal">
        If you have any questions about this payslip, please contact HR.
        This payslip is system-generated and does not require a stamp.
        Queries must be raised with the HR office within 14 days of issue.
      </div>
    </section>
  `;
  };

  const html = `<!doctype html><html><head>
    <meta charset="utf-8" />
    <title>Payslip ${payslips.length === 1 ? safe(payslips[0].employee_name) : `${payslips.length} payslips · ${safe(periodLabel)}`}</title>
    <style>
      /* ────────────────────────────────────────────────────────────
         Payslip stylesheet — Vertex42-inspired traditional layout:
         soft blue banners, alternating-shade rows, single accent
         colour (#4a76b8) for all section headers, dark GROSS PAY /
         TOTAL DEDUCTIONS bar rows. Preserves every data field the
         previous template rendered (allowance breakdown, PAYE
         bracket walk, employer contribution, signatures, watermark).
         ──────────────────────────────────────────────────────────── */
      @page { size: A4 portrait; margin: 12mm; }
      * { box-sizing: border-box; }
      body { margin: 0;
        font-family: 'Inter', 'Helvetica Neue', Arial, sans-serif;
        color: #1f2937;
        -webkit-print-color-adjust: exact; print-color-adjust: exact; }

      .page { position: relative; min-height: 250mm; padding: 2mm 2mm 8mm; overflow: hidden;
              page-break-after: always; }
      .page:last-child { page-break-after: auto; }

      /* Watermark — school logo, big and pale, behind everything. */
      .watermark {
        position: absolute; inset: 0; margin: auto;
        width: 70%; height: 70%;
        background-position: center; background-repeat: no-repeat; background-size: contain;
        opacity: 0.05;
        transform: rotate(-18deg);
        pointer-events: none; z-index: 0;
      }
      .page > *:not(.watermark) { position: relative; z-index: 1; }

      /* ── Header (school on left, big PAYSLIP wordmark on right) ── */
      header.hdr {
        display: flex; justify-content: space-between; align-items: flex-start;
        gap: 14px; padding-bottom: 10px;
        border-bottom: 2px solid #4a76b8;
      }
      .hdr-left { display: flex; gap: 12px; align-items: flex-start; }
      .logo { width: 60px; height: 60px; object-fit: contain; flex-shrink: 0; }
      .school { font-size: 20pt; font-weight: 900; color: #2c5282; letter-spacing: 0.2px; line-height: 1.05; }
      .motto  { font-size: 9pt; color: #555; font-style: italic; margin-top: 2px; }
      .meta   { font-size: 8.5pt; color: #4a5568; margin-top: 2px; }
      .hdr-tag {
        font-size: 22pt; font-weight: 900; color: #2c5282;
        letter-spacing: 1.5px;
      }

      /* ── Two-column info card (employee + pay-period grid) ────── */
      .top {
        display: grid; grid-template-columns: 1fr 1.15fr; gap: 8px;
        margin-top: 10px;
      }
      .emp-card { border: 1px solid #cfd8e3; }
      .band {
        background: #7ea3cf; color: #fff;
        font-size: 9pt; font-weight: 800; letter-spacing: 0.8px;
        padding: 5px 8px;
      }
      .emp-body { padding: 8px 10px; font-size: 10pt; line-height: 1.35; }
      .emp-name { font-weight: 800; font-size: 11pt; margin-bottom: 2px; }
      .emp-line { color: #333; }
      .emp-line.small { font-size: 9pt; color: #555; }

      .paygrid { border: 1px solid #cfd8e3; display: flex; flex-direction: column; }
      .paygrid-row { display: grid; grid-template-columns: 1fr 1fr 1fr; }
      .paygrid-row.band-row .cell {
        background: #7ea3cf; color: #fff;
        font-size: 8.5pt; font-weight: 800; letter-spacing: 0.6px;
      }
      .paygrid .cell {
        padding: 5px 8px; border-right: 1px solid #cfd8e3;
        text-align: center; font-size: 9.5pt;
      }
      .paygrid .cell:last-child { border-right: none; }
      .paygrid .cell.v { font-weight: 700; color: #1f2937; }
      .paygrid-row + .paygrid-row .cell { border-top: 1px solid #cfd8e3; }

      /* ── "Payment Method:" one-line row under the info block ─── */
      .pay-method {
        margin-top: 10px; font-size: 10pt; color: #333;
        padding-bottom: 4px;
      }
      .pm-lbl { font-weight: 800; color: #2c5282; margin-right: 6px; }
      .pm-v   { font-weight: 500; }

      /* ── Ledger tables (Earnings / Deductions / PAYE / Employer) */
      table.ledger { width: 100%; border-collapse: collapse; margin-top: 10px;
        border: 1px solid #cfd8e3; }
      table.ledger thead th {
        background: #7ea3cf; color: #fff;
        padding: 5px 8px; text-align: left;
        font-size: 9pt; font-weight: 800; letter-spacing: 0.6px;
      }
      table.ledger thead th.amt { text-align: right; }
      table.ledger td { padding: 5px 8px; font-size: 10pt; border-top: 1px solid #eef0f4; }
      table.ledger .amt { text-align: right; font-variant-numeric: tabular-nums; }
      /* YTD column gets a soft grey tint so eye can tell it apart. */
      table.ledger .ytd { background: #edf0f4; }
      /* GROSS PAY / TOTAL DEDUCTIONS / TOTAL EMPLOYER COST — dark bar row. */
      table.ledger tr.grand td {
        background: #5e7ba0; color: #fff;
        font-weight: 800; letter-spacing: 0.5px;
        padding: 6px 8px; border-top: none;
      }
      table.ledger tr.grand td.ytd { background: #4d688a; }
      /* Allowance chips inline with the description. */
      table.ledger .tag {
        display: inline-block;
        font-size: 7pt; font-weight: 800; letter-spacing: 0.4px;
        padding: 1px 5px; margin-left: 4px;
        border-radius: 8px; text-transform: uppercase;
      }
      table.ledger .tag.taxable    { background: #fef3c7; color: #92400e; }
      table.ledger .tag.pensionable{ background: #dbeafe; color: #1e40af; }

      /* PAYE bracket walk — keeps its slightly darker banner so it's
         visually distinct from the Earnings/Deductions blocks. */
      table.ledger.paye thead th { background: #5e7ba0; }
      table.ledger.paye tr.meta-row td {
        background: #f6f8fb; color: #444; font-size: 9pt;
      }
      table.ledger.paye .muted { color: #888; font-size: 9pt; }
      table.ledger.paye .muted.small { display: block; font-size: 8pt; font-style: italic; }
      table.ledger.paye tr.tot td {
        font-weight: 800; background: #eef2f7;
        border-top: 2px solid #cfd8e3;
      }

      /* Employer contribution — muted banner to signal informational. */
      table.ledger.employer thead th { background: #98a9bf; }

      /* NET PAY strip — full-width dark row, biggest number on the slip. */
      table.ledger.net { margin-top: 10px; border: 2px solid #2c5282; }
      table.ledger.net .net-l {
        background: #2c5282; color: #fff; font-weight: 900;
        letter-spacing: 1.2px; font-size: 12pt; padding: 8px 10px;
      }
      table.ledger.net .net-v {
        font-size: 14pt; font-weight: 900; color: #1b5e20;
        padding: 8px 10px; background: #fff;
      }
      table.ledger.net .net-v.ytd { background: #edf0f4; }

      .footnote { margin-top: 10px; font-size: 9pt; color: #555; }

      .sigs { margin-top: 18mm; display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 16px; }
      .sig .line { border-top: 1px solid #444; height: 18px; }
      .sig .lbl  { text-align: center; font-size: 9pt; color: #555; font-weight: 700; }

      .legal { margin-top: 6mm; font-size: 8pt; color: #6b7280; text-align: center;
        border-top: 1px solid #e5e7eb; padding-top: 4px; line-height: 1.5; }
    </style>
  </head><body>
    ${payslips.map(p => pageFor(p)).join('')}
  </body></html>`;

  const w = window.open('', '_blank', 'width=900,height=1000');
  if (!w) return;
  w.document.write(html);
  w.document.close();
  w.focus();
  // Give the watermark image (if any) a moment to load before triggering print.
  setTimeout(() => w.print(), 350);
}
